import { useState } from "react";
import { Building2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field, Select, Modal } from "@/components/common";
import { LANGS, TONES } from "@/pages/Composer";

const FIELDS = [["business_name", "Business name"], ["sender_name", "Sender name"], ["reply_to", "Reply-to email (optional with own mailbox)"], ["website", "Website"]];
export const NEW_SENDER = { business_name: "", default_language: "English", default_tone: "professional" };
export const RETURN_KEY = "ringus_return_to";

export function SenderDialog({ value, onClose, onSaved }) {
  const [edit, setEdit] = useState(value);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setEdit({ ...edit, [k]: v });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = edit.id ? await api.put(`/senders/${edit.id}`, edit) : await api.post("/senders", edit);
      toast.success("Business saved");
      onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Modal open onClose={onClose} title={edit.id ? "Edit business profile" : "Add business"} wide testid="sender-dialog">
      <div className="grid gap-3 sm:grid-cols-2">
        {FIELDS.map(([k, l]) => <Field key={k} label={l}><input data-testid={`sender-${k}-input`} className="cd-input" value={edit[k] || ""} onChange={(e) => set(k, e.target.value)} /></Field>)}
        <div className="sm:col-span-2"><Field label="Business description (used by AI — facts only)"><textarea data-testid="sender-description-input" rows={3} className="cd-textarea" value={edit.description || ""} onChange={(e) => set("description", e.target.value)} /></Field></div>
        <div className="sm:col-span-2"><Field label="Products & services"><textarea data-testid="sender-products-input" rows={2} className="cd-textarea" value={edit.products_services || ""} onChange={(e) => set("products_services", e.target.value)} /></Field></div>
        <div className="sm:col-span-2"><Field label="Signature"><textarea data-testid="sender-signature-input" rows={3} className="cd-textarea" value={edit.signature || ""} onChange={(e) => set("signature", e.target.value)} /></Field></div>
        <Field label="Default language"><Select value={edit.default_language} onChange={(v) => set("default_language", v)} options={LANGS} /></Field>
        <Field label="Default tone"><Select value={edit.default_tone} onChange={(v) => set("default_tone", v)} options={TONES} /></Field>
        <p className="text-xs text-slate-500 sm:col-span-2">Changing the reply-to address pauses sending until you turn it on again.</p>
        <button data-testid="sender-save-btn" disabled={busy || !edit.business_name?.trim()} onClick={save} className="cd-btn-primary sm:col-span-2">{busy ? "Saving…" : "Save business"}</button>
      </div>
    </Modal>
  );
}

export function SenderSetupCard({ onCreated, onLater, testid = "sender-setup-card" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="cd-card border-teal-200 bg-teal-50/40" data-testid={testid}>
      <p className="flex items-center gap-2 font-bold"><Building2 className="h-5 w-5 text-teal-700" />No sender business yet</p>
      <p className="mt-1 text-sm text-slate-600" data-testid="sender-setup-message">Want to send introduction emails? Add your business and connect your email account.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button data-testid="sender-setup-add-btn" onClick={() => setOpen(true)} className="cd-btn-primary"><Plus className="h-4 w-4" />Add business</button>
        <button data-testid="sender-setup-later-btn" onClick={onLater} className="cd-btn-ghost">Set up later</button>
      </div>
      {open && <SenderDialog value={NEW_SENDER} onClose={() => setOpen(false)} onSaved={(s) => { setOpen(false); onCreated(s); }} />}
    </div>
  );
}
